/**
 * Group Adjustments (FSD 2.2.7 "Post Adjustments", FIN-F07-V06): a balanced journal over
 * group-chart accounts, recorded on one consolidation run, approved by the Group
 * Controller step in the Approvals Inbox, and part of the run's statements only once
 * approved.
 *
 * Reading needs `consol.adjustment.read`. Preparing, editing, submitting and withdrawing
 * are gated on the action by `consol.adjustment.create`; deciding is not done here at all
 * (the Approvals Inbox, `consol.adjustment.approve`).
 *
 * The `:id` is constrained to digits so `/consol/adjustments/new` is never parsed as an
 * adjustment number.
 */

import type { RouteRecordRaw } from 'vue-router'

export const CONSOL_ADJUSTMENT_PERMISSIONS = {
  read: 'consol.adjustment.read',
  create: 'consol.adjustment.create',
  approve: 'consol.adjustment.approve',
} as const

export const CONSOL_ADJUSTMENT_PATHS = {
  list: '/consol/adjustments',
  listForRun: (runId: number) => `/consol/adjustments?runId=${runId}`,
  create: (runId?: number | null) =>
    runId === undefined || runId === null ? '/consol/adjustments/new' : `/consol/adjustments/new?runId=${runId}`,
  one: (id: number) => `/consol/adjustments/${id}`,
  edit: (id: number) => `/consol/adjustments/${id}/edit`,
} as const

export const CONSOL_ADJUSTMENT_ROUTES: RouteRecordRaw[] = [
  {
    path: '/consol/adjustments',
    name: 'consol-adjustments',
    component: () => import('@/modules/consol/adjustments/AdjustmentsListView.vue'),
    meta: {
      titleKey: 'consol.routeTitles.adjustments',
      permission: CONSOL_ADJUSTMENT_PERMISSIONS.read,
    },
  },
  {
    path: '/consol/adjustments/new',
    name: 'consol-adjustment-new',
    component: () => import('@/modules/consol/adjustments/AdjustmentFormView.vue'),
    meta: {
      titleKey: 'consol.routeTitles.adjustmentNew',
      permission: CONSOL_ADJUSTMENT_PERMISSIONS.create,
    },
  },
  {
    path: '/consol/adjustments/:id(\\d+)',
    name: 'consol-adjustment',
    component: () => import('@/modules/consol/adjustments/AdjustmentView.vue'),
    meta: {
      titleKey: 'consol.routeTitles.adjustment',
      permission: CONSOL_ADJUSTMENT_PERMISSIONS.read,
    },
  },
  {
    path: '/consol/adjustments/:id(\\d+)/edit',
    name: 'consol-adjustment-edit',
    component: () => import('@/modules/consol/adjustments/AdjustmentFormView.vue'),
    meta: {
      titleKey: 'consol.routeTitles.adjustmentEdit',
      permission: CONSOL_ADJUSTMENT_PERMISSIONS.create,
    },
  },
]
